import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@copro/api";
import { Database } from "@copro/api";
import { keys } from "./keys";
import { keys as documentKeys } from "../../documents/api/keys";
import { syncOwnedDocument } from "../../documents/api/syncOwnedDocument";
import { useSpace } from "../../../hooks/useSpace";

// file_url is not a contract column anymore: it lives in a linked document.
type Command = Database['public']['Tables']['provider_contracts']['Update'] & {
  file_url?: string | null;
};

async function putProviderContract(
  contractId: string,
  providerId: string,
  currentDocumentId: string | null,
  command: Command,
  spaceId: string,
) {
  const { file_url, ...contract } = command;

  const documentId = await syncOwnedDocument({
    currentDocumentId,
    fileUrl: file_url ?? null,
    name: contract.name ?? '',
    type: 'Contrat',
    ownerColumn: 'provider_id',
    ownerId: providerId,
    spaceId,
    onBeforeDelete: async () => {
      // document_id is ON DELETE RESTRICT: unlink before the document is deleted.
      await apiClient
        .from('provider_contracts')
        .update({ document_id: null })
        .eq('id', contractId);
    },
  });

  const { data, error } = await apiClient
    .from('provider_contracts')
    .update({ ...contract, document_id: documentId })
    .eq('id', contractId)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

type Variables = {
  providerId: string;
  contractId: string;
  currentDocumentId: string | null;
  command: Command;
}

export default function usePutProviderContract() {
  const queryClient = useQueryClient();
  const { currentSpace } = useSpace();

  return useMutation({
    mutationFn: ({ providerId, contractId, currentDocumentId, command }: Variables) => {
      if (!currentSpace) {
        throw new Error('Aucun espace sélectionné pour enregistrer le contrat.');
      }

      return putProviderContract(contractId, providerId, currentDocumentId, command, currentSpace.id);
    },
    onSuccess: (_, { providerId }: Variables) => {
      queryClient.invalidateQueries({ queryKey: keys.getProviderContracts(providerId) });
      // The contract file also lives in documents (unified list, provider list/count).
      queryClient.invalidateQueries({ queryKey: documentKeys.documents });
    },
  });
}
